import { useEffect } from 'react';
import { usePreviewMode } from '../contexts/PreviewModeContext';

const ASSESSMENT_CONTAINER_SELECTOR = '#learnosity_assess';

const elementFor = (node: Node | null): Element | null =>
  node === null ? null : node.nodeType === Node.ELEMENT_NODE ? (node as Element) : node.parentElement;

// Whether the live selection overlaps the assessment at all — NOT where it starts. A select-all
// anchors at <body>, and a selection dragged downward from the page heading into the problem
// anchors in the heading; both put item text on the clipboard while starting outside the
// container, so an anchor-based check would miss exactly the broadest copies. Overlap catches a
// selection entering the container from either direction.
//
// The loop covers Firefox, the one engine that supports multiple selection ranges.
const selectionOverlapsAssessment = (): boolean => {
  const container = document.querySelector(ASSESSMENT_CONTAINER_SELECTOR);
  const selection = window.getSelection();
  if (container === null || !selection) return false;

  for (let i = 0; i < selection.rangeCount; i += 1) {
    if (selection.getRangeAt(i).intersectsNode(container)) return true;
  }
  return false;
};

// A `copy` event is NOT dispatched at the element whose text is selected: per spec the target is
// the selection's start node's nearest element, and browsers may use <body> or the focused element
// instead. So `event.target` alone is unreliable — it is only the fast path for an event with no
// selection behind it, such as a cut from an answer field.
//
// Caveat: Firefox does not reflect a selection inside an <input>/<textarea> in
// window.getSelection(), so a copy from a text field OUTSIDE the container would be judged by
// whatever document selection is left over and could be cancelled. Harmless today — these three
// screens have no text input outside #learnosity_assess — but worth re-checking if one is added.
const isInsideAssessment = (event: Event): boolean =>
  Boolean(elementFor(event.target as Node | null)?.closest(ASSESSMENT_CONTAINER_SELECTOR)) ||
  selectionOverlapsAssessment();

// Blocks clipboard extraction of assessment content without disabling text selection (TEAMF-1378).
//
// The original cheating mitigation used `user-select: none` on the Learnosity container, which
// works only as a side effect: killing selection kills copy. But selection is also what
// Learnosity's text-to-speech reads from, so students lost the ability to have one passage read
// aloud — TTS fell back to the whole page, unlike practice problems. Cancelling the clipboard
// events directly keeps selection (and therefore TTS, find-in-page and screen-reader navigation)
// intact while still denying the copy.
//
// `dragstart` is deliberately NOT cancelled. Dragging a selection into another tab is a
// clipboard-free extraction path, but Learnosity's drag-and-drop question types are answered by
// dragging response tokens into dropzones, and the risk of making a question unanswerable
// outweighs closing a niche extraction path.
//
// This is a deterrent, not a control — devtools, screenshots and OCR all defeat it, as they
// defeated `user-select: none`. The threat model is unchanged; only the accessibility cost is.
export const useCopyProtection = () => {
  const previewMode = usePreviewMode();

  useEffect(() => {
    // Preview mode is the teacher-facing guided tour over the same components. Teachers aren't the
    // threat and may legitimately want to copy an item's text, matching how
    // useSuspiciousActivityTracking opts preview out.
    if (previewMode) return;

    const blockClipboardWrite = (event: Event) => {
      if (!isInsideAssessment(event)) return;
      event.preventDefault();
    };

    // Delegated on `document` and filtered by container, because Learnosity owns the DOM inside
    // #learnosity_assess and recreates it on every item change — the same approach the paste
    // listener in useSuspiciousActivityTracking uses.
    document.addEventListener('copy', blockClipboardWrite);
    document.addEventListener('cut', blockClipboardWrite);

    return () => {
      document.removeEventListener('copy', blockClipboardWrite);
      document.removeEventListener('cut', blockClipboardWrite);
    };
  }, [previewMode]);
};
